/**
 * Shared location types for API contracts between frontend and backend
 */
import { z } from "zod";
import { DayPartsSchema } from "./companySettings.js";
import { uuidSchema, phoneSchema } from "./validation-common.js";
// ===== LOCATION MANAGER TYPES =====
export const LocationManagerInfoSchema = z.object({
    id: z.uuid(),
    employmentId: z.uuid(),
    userId: z.uuid(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    createdAt: z.iso.datetime(),
});
// ===== VALIDATION SCHEMAS =====
// Time format regex: HH:mm (24-hour format, no seconds)
const timeFormatRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
const OperatingHoursPeriodSchema = z
    .object({
    startTime: z
        .string()
        .regex(timeFormatRegex, "Time must be in HH:mm format (e.g., 09:00)"),
    endTime: z
        .string()
        .regex(timeFormatRegex, "Time must be in HH:mm format (e.g., 17:00)"),
})
    .refine((data) => {
    // Validate startTime < endTime
    const [startHour, startMin] = data.startTime.split(":").map(Number);
    const [endHour, endMin] = data.endTime.split(":").map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return startMinutes < endMinutes;
}, { message: "closing hours must be set after opening hours" });
export const OperatingHoursSchema = z
    .object({
    MONDAY: z
        .array(OperatingHoursPeriodSchema)
        .max(10, "Maximum 10 periods per day")
        .optional(),
    TUESDAY: z
        .array(OperatingHoursPeriodSchema)
        .max(10, "Maximum 10 periods per day")
        .optional(),
    WEDNESDAY: z
        .array(OperatingHoursPeriodSchema)
        .max(10, "Maximum 10 periods per day")
        .optional(),
    THURSDAY: z
        .array(OperatingHoursPeriodSchema)
        .max(10, "Maximum 10 periods per day")
        .optional(),
    FRIDAY: z
        .array(OperatingHoursPeriodSchema)
        .max(10, "Maximum 10 periods per day")
        .optional(),
    SATURDAY: z
        .array(OperatingHoursPeriodSchema)
        .max(10, "Maximum 10 periods per day")
        .optional(),
    SUNDAY: z
        .array(OperatingHoursPeriodSchema)
        .max(10, "Maximum 10 periods per day")
        .optional(),
})
    .optional();
// ===== LOCATION FIELD SCHEMAS =====
const addressLine1Schema = z.string().trim().min(1).max(255);
const addressLine2Schema = z.string().trim().max(255);
const citySchema = z.string().trim().min(1).max(100);
const stateSchema = z.string().regex(/^[A-Z]{2}$/, "Must be 2-letter state code");
const zipCodeSchema = z.string().regex(/^\d{5}(-\d{4})?$/, "Must be 12345 or 12345-6789");
const countrySchema = z.string().trim().min(1).max(100);
const latitudeSchema = z.number().min(-90).max(90);
const longitudeSchema = z.number().min(-180).max(180);
const websiteSchema = z
    .string()
    .trim()
    .max(500)
    .regex(/^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/\S*)?$/i, "Must be a valid URL (e.g., https://www.example.com)")
    .optional();
const storeNumberSchema = z.string().trim().max(50).optional();
const einSchema = z.string().trim().max(20).optional(); // EIN format: XX-XXXXXXX
const nameSchema = z.string().trim().min(1).max(255).optional();
const locationTypeSchema = z.string().trim().max(50).optional(); // "PHYSICAL", "MOBILE", etc.
/**
 * IANA timezone validation
 * Validates against the official IANA timezone database using Intl.DateTimeFormat
 * Examples: "America/New_York", "Europe/London", "Asia/Tokyo"
 */
const timezoneSchema = z
    .string()
    .trim()
    .refine((tz) => {
    try {
        // Intl.DateTimeFormat throws if timezone is invalid
        new Intl.DateTimeFormat("en-US", { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, { message: "Must be a valid IANA timezone (e.g., 'America/New_York')" });
/**
 * Manager count validation
 * Configurable limit for maximum managers per location
 * Bounds: 1-20 (reasonable range for any location)
 */
const managerCountSchema = z
    .number()
    .int("Must be a whole number")
    .min(1, "Must allow at least 1 manager")
    .max(20, "Cannot exceed 20 managers");
// ===== JSON FIELD SCHEMAS =====
// External IDs from various systems (Rippling, custom location ID, etc.)
export const ExternalIdsSchema = z
    .object({
    ripplingId: z.string().optional(),
    locationId: z.string().optional(), // User-entered location identifier
    otherIds: z.record(z.string(), z.string()).optional(), // Extensible key-value pairs
})
    .optional();
// Physical store dimensions and capacity
export const StoreDimensionsSchema = z
    .object({
    squareFootage: z.number().positive().optional(),
    seats: z.number().int().nonnegative().optional(),
})
    .optional();
// ===== LOCATION INPUT SCHEMAS =====
export const CreateLocationInputSchema = z.object({
    // Required fields
    addressLine1: addressLine1Schema,
    city: citySchema,
    state: stateSchema,
    zipCode: zipCodeSchema,
    companyId: uuidSchema,
    phone: phoneSchema.optional(),
    // Optional fields
    addressLine2: addressLine2Schema.optional(),
    country: countrySchema.optional(),
    latitude: latitudeSchema.optional(),
    longitude: longitudeSchema.optional(),
    timezone: timezoneSchema.optional(),
    managerUserIds: z.array(uuidSchema).optional(),
    managerCount: managerCountSchema.optional(), // Defaults to 1 in database
    // Location details
    name: nameSchema,
    locationType: locationTypeSchema,
    website: websiteSchema,
    storeNumber: storeNumberSchema,
    ein: einSchema,
    externalIds: ExternalIdsSchema,
    storeDimensions: StoreDimensionsSchema,
    operatingHours: OperatingHoursSchema,
    dayParts: DayPartsSchema.optional(),
});
export const UpdateLocationInputSchema = z.object({
    addressLine1: addressLine1Schema.optional(),
    addressLine2: addressLine2Schema.nullable().optional(),
    city: citySchema.optional(),
    state: stateSchema.optional(),
    zipCode: zipCodeSchema.optional(),
    country: countrySchema.optional(),
    phone: phoneSchema.optional(),
    latitude: latitudeSchema.nullable().optional(),
    longitude: longitudeSchema.nullable().optional(),
    timezone: timezoneSchema.nullable().optional(),
    operatingHours: OperatingHoursSchema.nullable(),
    managerCount: managerCountSchema.optional(),
    // Location details
    name: nameSchema.nullable().optional(),
    locationType: locationTypeSchema.nullable().optional(),
    website: websiteSchema.nullable().optional(),
    storeNumber: storeNumberSchema.nullable().optional(),
    ein: einSchema.nullable().optional(),
    externalIds: ExternalIdsSchema.nullable().optional(),
    storeDimensions: StoreDimensionsSchema.nullable().optional(),
    dayParts: DayPartsSchema.nullable().optional(),
});
// API Response type for Location (based on Prisma Location model, but pure API type)
export const LocationResponseSchema = z.object({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    addressLine1: z.string(),
    addressLine2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
    country: z.string(),
    phone: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    companyId: z.uuid(),
    timezone: z.string().nullable(),
    operatingHours: OperatingHoursSchema.nullable(),
    managers: z.array(LocationManagerInfoSchema),
    managerCount: z.number().int(),
    // Active status
    isActive: z.boolean(),
    // POS Integration (read-only)
    sourceSystem: z.string().nullable(),
    sourceSystemId: z.string().nullable(),
    // Location details
    name: z.string().nullable(),
    locationType: z.string().nullable(),
    website: z.string().nullable(),
    storeNumber: z.string().nullable(),
    ein: z.string().nullable(),
    externalIds: ExternalIdsSchema.nullable(),
    storeDimensions: StoreDimensionsSchema.nullable(),
    dayParts: DayPartsSchema.optional(),
});
// ===== LOCATION MANAGER ROUTE SCHEMAS =====
export const AddLocationManagerInputSchema = z.object({
    userId: uuidSchema,
});
export const LocationManagerParamSchema = z.object({
    id: uuidSchema,
    userId: uuidSchema,
});
